import Brand from '@/components/Brand';
import FullScreenImageModal from '@/components/FullscreenImageModal';
import ScrolledCard from '@/components/ScrolledCard';
import StudentConfirmDialog, { StudentConfirmDialogPropsType } from '@/components/StudentConfirmDialog';
import Constants from '@/Constants';
import { useQuestionnaire } from '@/hooks/questionnaire/useQuestionnaire';
import { useQuestionnaires } from '@/hooks/questionnaire/useQuestionnaires';
import { useCourseStudentView } from '@/hooks/useCourseStudentView';
import { EvaluationLesson } from '@/models/Lesson';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { AnswerResult, Question } from '@/models/Question';
import { Questionnaire } from '@/models/Questionnaire';
import { Status } from '@/models/Status';
import http from '@/services/HttpService';
import { lmsCourseService } from '@/services/LMSCourseService';
import { ApplicationState } from '@/store';
import { hideHeader, showHeader } from '@/store/header/action';
import { finishQuestionnaireRequest } from '@/store/questionnaire/action';
import { LocaleData } from '@/translations/LocaleData';
import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import QuestionnaireFeedback, { QuestionnaireFeedbackProps } from './QuestionnaireFeedback';
import QuestionnaireQuestions from './QuestionnaireQuestions';
import './style.scss';

const completedStatus: Status[] = ['PASSED', 'FAILED'];

const QuestionnaireScreen: React.FC = () => {
    const { courseId: courseIdString, lessonId: lessonIdString, enrollmentId: enrollmentIdString } = useParams<{
        courseId: string;
        lessonId: string;
        enrollmentId: string;
    }>();

    const lessonId: number = Number(lessonIdString);
    const courseId: number = Number(courseIdString);
    const classEnrollmentId: number = Number(enrollmentIdString);

    const personId: number = useSelector((state: ApplicationState) => state.identity.data.person.id || 0);

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const responseSummary: AnswerResult[] = useSelector((state: ApplicationState) => state.questionnaire.responseSummary);

    const question: Question = useSelector((state: ApplicationState) => state.questionnaire?.question);

    const [questionnaireId, setQuestionnaireId] = useState<number>();

    const [feedbackProps, setFeedbackProps] = useState<QuestionnaireFeedbackProps>();

    const [showConfirmation, setShowConfirmation] = useState(false);

    const [modalConfig, setModalConfig] = useState<StudentConfirmDialogPropsType>({} as StudentConfirmDialogPropsType);

    const [fullScreenImage, setFullscreenImage] = useState<boolean>(false);

    const dispatch: Dispatch = useDispatch();

    const { courseEnrollment, loading: courseStudentViewLoading, getLessonEnrollment, clearCourseStudentView } = useCourseStudentView({
        moduleId: Constants.LMS_MODULE_ID,
        courseId,
        classEnrollmentId,
    });

    const lessonEnrollment: LessonEnrollment = getLessonEnrollment(lessonId);

    const evaluationLesson = lessonEnrollment.lesson as EvaluationLesson;

    const { questionnaires = [], loading: questionnairesLoading, clear: clearQuestionnaires, reloadQuestionnaires } = useQuestionnaires({
        evaluationId: evaluationLesson?.evaluationId,
        classEnrollmentId,
    });

    const { questionnaire, clear: clearQuestionnaire } = useQuestionnaire(questionnaireId);

    const isCourseFromImport: boolean = Boolean(courseEnrollment.course?.dynamicAttributes?.createdFromIntegrationProcess);

    const history = useHistory();

    const navigateToCourse = () => {
        history.push(`/lms/enrollments/${Number(classEnrollmentId)}/courses/${courseId}`);
    };

    const exitConfirmation = () => {
        if (questionnaireId) {
            dispatch(finishQuestionnaireRequest(questionnaireId));
        }
        navigateToCourse();
    };

    const onBackToCourseHandler = () => {
        setModalConfig({
            leftButtonTitle: strings.NO,
            leftClick: () => setShowConfirmation(false),
            rightButtonTitle: strings.YES,
            rightClick: () => navigateToCourse(),
            title: strings.QUESTIONNAIRE_RESULTS.DO_YOU_WANT_TO_GO_BACK_TO_COURSE,
        } as StudentConfirmDialogPropsType);
        setShowConfirmation(true);
    };

    const onExitEvaluationHandler = () => {
        setModalConfig({
            leftButtonTitle: strings.CONFIRM_EXIT,
            leftClick: () => exitConfirmation(),
            rightButtonTitle: strings.NO,
            rightClick: () => setShowConfirmation(false),
            title: strings.EXIT_EVALUATION_TITLE,
            content: strings.EXIT_EVALUATION_CONFIRMATION,
        } as StudentConfirmDialogPropsType);
        setShowConfirmation(true);
    };

    const clearAllOnUnmount = () => {
        return () => {
            clearCourseStudentView();
            clearQuestionnaires();
            clearQuestionnaire();
        };
    };

    const headerControl = () => {
        dispatch(hideHeader());

        return () => {
            dispatch(showHeader());
        };
    };

    const generateQuestionnaire = (): void => {
        const path: string = `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}?entityType=lesson&entityId=${lessonId}`;

        http.post(path, {
            evaluation: { id: evaluationLesson.evaluationId },
            personId,
            evaluatedEntityType: 'lesson',
            evaluatedEntityId: lessonId,
            attemptNumber: questionnaires.length + 1,
            dynamicAttributes: { classEnrollmentId },
        }).then((response: AxiosResponse) => {
            startQuestionnaire(response.data.id);
        });
    };

    const startQuestionnaire = (questionnaireId: number): void => {
        const path = `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}/${questionnaireId}/start`;

        http.post(path).then((response: AxiosResponse) => {
            setQuestionnaireId(response.data.id);
            reloadQuestionnaires();
        });
    };

    const getPendingQuestionnaire = (): Questionnaire | undefined => questionnaires.find(({ score }) => score === null);

    const isQuestionnaireCompleted = (): boolean => completedStatus.some((completedStatus) => completedStatus === lessonEnrollment.status);

    const hasAttemptsLeft = (): boolean => {
        const { attempts = 0, grade = 0 } = lessonEnrollment;
        return lmsCourseService.attemptsLeftInEvaluation(evaluationLesson, attempts, grade) > 0;
    };

    const getQuestionnaireId = () => {
        if (courseStudentViewLoading || questionnairesLoading) {
            return;
        }

        const pendingQuestionnaire: Questionnaire | undefined = getPendingQuestionnaire();

        if (pendingQuestionnaire) {
            setQuestionnaireId(pendingQuestionnaire.id);
            return;
        }

        const isCompleted: boolean = isQuestionnaireCompleted();
        const hasAttempts: boolean = hasAttemptsLeft();

        if (isCompleted && !hasAttempts) {
            if (isCourseFromImport) {
                configureFeedbackPropsFromImport();
            } else {
                const completedQuestionnaireId: number = getCompletedQuestionnaireId();
                setQuestionnaireId(completedQuestionnaireId);

                const { grade: score } = lessonEnrollment;
                configureFeedbackProps({ attemptsLeft: 0, score, questionnaireId: completedQuestionnaireId } as QuestionnaireFeedbackProps);
            }
            return;
        }

        startLesson();
    };

    const getCompletedQuestionnaireId = (): number => {
        let id: number;

        if (evaluationLesson.saveHighestScore) {
            const questionnaireWithHighestScore = getQuestionnaireWithHighestScore();
            id = questionnaireWithHighestScore.id;
        } else {
            id = questionnaires[questionnaires.length - 1].id;
        }

        return id;
    };

    const getQuestionnaireWithHighestScore = (): Questionnaire => {
        const questionnaire = questionnaires.reduce((previousQuestionnaire, currentQuestionnaire) => {
            const { score: previousScore } = previousQuestionnaire;
            const { score: currentScore } = currentQuestionnaire;

            return previousScore > currentScore ? previousQuestionnaire : currentQuestionnaire;
        }, questionnaires[0]);

        return questionnaire;
    };

    const startLesson = (): void => {
        const pendingQuestionnaire: Questionnaire | undefined = getPendingQuestionnaire();
        const isCompleted: boolean = isQuestionnaireCompleted();
        const hasAttempts: boolean = hasAttemptsLeft();

        if (pendingQuestionnaire || (isCompleted && !hasAttempts)) {
            return;
        }

        const path: string = Constants.START_LESSON_URI.replace('%s', String(lessonId));
        http.post(path, { classEnrollmentId, lessonId }).then(generateQuestionnaire);
    };

    const handleOnConcludeQuestionnaire = useCallback((): void => {
        if (questionnaire && evaluationLesson && lessonEnrollment) {
            const attemptsLeft = lmsCourseService.attemptsLeftInEvaluation(evaluationLesson, lessonEnrollment.attempts + 1, questionnaire.score);
            configureFeedbackProps({ attemptsLeft, score: Math.round(questionnaire.score) } as QuestionnaireFeedbackProps);
        }
    }, [evaluationLesson, lessonEnrollment, questionnaire]);

    const configureFeedbackPropsFromImport = (): void => {
        const { attempts, grade: score } = lessonEnrollment;
        const attemptsLeft = lmsCourseService.attemptsLeftInEvaluation(evaluationLesson, attempts, score);

        if (attemptsLeft <= 0) {
            configureFeedbackProps({
                attemptsLeft: 0,
                questionnaireId: 0,
                score,
                showResponseSummary: false,
            } as QuestionnaireFeedbackProps);
        }
    };

    const configureFeedbackProps = (feedbackProps: QuestionnaireFeedbackProps): void => {
        const { masteryScore, showResponseSummary } = evaluationLesson;

        const defaultFeedbackProps = {
            courseId,
            lessonId,
            masteryScore,
            showResponseSummary,
            questionnaireId,
        } as QuestionnaireFeedbackProps;

        setFeedbackProps({ ...defaultFeedbackProps, ...feedbackProps });
    };

    useEffect(headerControl, []);

    useEffect(clearAllOnUnmount, []);

    useEffect(getQuestionnaireId, [
        questionnaires,
        lessonEnrollment.status,
        lessonEnrollment.attempts,
        lessonEnrollment.grade,
        evaluationLesson?.saveHighestScore,
        courseStudentViewLoading,
        questionnairesLoading,
    ]);

    return (
        <div className="question-answer">
            <StudentConfirmDialog
                content={modalConfig.content}
                leftButtonTitle={modalConfig.leftButtonTitle}
                leftClick={() => modalConfig && modalConfig.leftClick && modalConfig.leftClick()}
                rightButtonTitle={modalConfig.rightButtonTitle}
                rightClick={() => modalConfig.rightClick()}
                show={showConfirmation}
                title={modalConfig.title}
            />

            <section className="d-flex flex-column question-answer__top">
                <div className="container question-answer__header">
                    <label>
                        <strong>{strings.KNOWLEDGE_EVALUATION}</strong>
                        <i className="material-icons">{Constants.icons.CIRCLE}</i>
                        <span>{evaluationLesson?.name || ''}</span>
                    </label>
                    {!feedbackProps ? (
                        <button className="btn btn-light nowrap" onClick={onExitEvaluationHandler}>
                            {strings.EXIT_EVALUATION}
                        </button>
                    ) : (
                        <button
                            className={`btn ${
                                lessonEnrollment?.status === Constants.contentEnrollmentStatus.PASSED ? 'btn-primary' : 'btn-light'
                            } nowrap`}
                            onClick={onBackToCourseHandler}
                        >
                            {strings.BACK_COURSE}
                        </button>
                    )}
                </div>
                <Brand />
            </section>

            <>
                {!feedbackProps && questionnaire && (
                    <QuestionnaireQuestions finished={handleOnConcludeQuestionnaire} questionnaireId={questionnaire.id} />
                )}

                {feedbackProps && (
                    <>
                        <ScrolledCard list={responseSummary}>
                            <QuestionnaireFeedback
                                attemptsLeft={feedbackProps.attemptsLeft}
                                classEnrollmentId={classEnrollmentId}
                                courseId={feedbackProps.courseId}
                                fullScreen={fullScreenImage}
                                lessonId={feedbackProps.lessonId}
                                masteryScore={feedbackProps.masteryScore}
                                onQuestionChange={() => setFullscreenImage(true)}
                                questionnaireId={feedbackProps.questionnaireId}
                                score={feedbackProps.score}
                                showResponseSummary={feedbackProps.showResponseSummary}
                            />
                        </ScrolledCard>

                        {question && question.id && (
                            <>
                                <FullScreenImageModal
                                    fullscreen={fullScreenImage}
                                    onCloseImage={() => setFullscreenImage(false)}
                                    path={question?.imagePath}
                                />
                            </>
                        )}
                    </>
                )}
            </>
        </div>
    );
};

export default QuestionnaireScreen;
